import type {
  partOtherProcess,
  partOtherProcessRow,
} from "@/api/breakdownService";
import { isValidDecimalInput, parseDecimal } from "@/utils/inputParse";
import { TableRow, TableCell, TextField } from "@mui/material";
import { useEffect, useState } from "react";

interface PartOtherProcessDataEditProps {
  row: partOtherProcessRow;
  setFormData: React.Dispatch<React.SetStateAction<partOtherProcess>>;
  type: "OEM" | "Spare";
  setFlaggedReason: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const PartOtherProcessDataEdit = ({
  row,
  setFormData,
  type,
  setFlaggedReason,
}: PartOtherProcessDataEditProps) => {
  const [priceInput, setPriceInput] = useState<string>(Number(row.price ?? 0).toFixed(2));

  useEffect(() => {
    setFormData((prev) => {
      const foundRow =
        type === "OEM"
          ? prev.oem_other_process?.find((r) => r.id === row.id)
          : prev.spare_other_process?.find((r) => r.id === row.id);
      if (!foundRow) return prev;
      const updatedRow: partOtherProcessRow = {
        ...foundRow,
        price: parseDecimal(priceInput, 2) || 0,
      };
      return {
        ...prev,
        oem_other_process:
          type === "OEM"
            ? [
                ...(prev.oem_other_process?.filter((r) => r.id !== row.id) ??
                  []),
                updatedRow,
              ]
            : prev.oem_other_process,
        spare_other_process:
          type === "Spare"
            ? [
                ...(prev.spare_other_process?.filter((r) => r.id !== row.id) ??
                  []),
                updatedRow,
              ]
            : prev.spare_other_process,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceInput, row.id, type]);

  return (
    <TableRow key={row.id} sx={{ backgroundColor: "#fff" }}>
      <TableCell
        sx={{
          width: "50%",
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        {row.process_name}
      </TableCell>
      <TableCell
        sx={{
          width: "30%",
          textAlign: "left",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <TextField
          value={row.detail}
          onChange={(e) => {
            const val = e.target.value;
            if (type === "OEM") {
              setFormData((prev) => {
                const foundRow = prev.oem_other_process?.find(
                  (r) => r.id === row.id,
                );
                if (!foundRow) return prev;
                const updatedRow: partOtherProcessRow = {
                  ...foundRow,
                  detail: val,
                };
                return {
                  ...prev,
                  oem_other_process: [
                    ...(prev.oem_other_process?.filter(
                      (r) => r.id !== row.id,
                    ) ?? []),
                    updatedRow,
                  ],
                };
              });
            } else {
              setFormData((prev) => {
                const foundRow = prev.spare_other_process?.find(
                  (r) => r.id === row.id,
                );
                if (!foundRow) return prev;
                const updatedRow: partOtherProcessRow = {
                  ...foundRow,
                  detail: val,
                };
                return {
                  ...prev,
                  spare_other_process: [
                    ...(prev.spare_other_process?.filter(
                      (r) => r.id !== row.id,
                    ) ?? []),
                    updatedRow,
                  ],
                };
              });
            }
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "left" },
          }}
        />
      </TableCell>
      <TableCell
        sx={{
          width: "30%",
          textAlign: "right",
          borderRight: "1px solid #e0e0e0",
        }}
      >
        <TextField
          value={priceInput}
          onChange={(e) => {
            const val = e.target.value;
            if (isValidDecimalInput(val, 2)) {
              setPriceInput(val.trim());
              if (type === "OEM") {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`OEM Part Other Process Value`),
                );
              } else {
                setFlaggedReason((prev) =>
                  new Set(prev).add(`Spare Part Other Process Value`),
                );
              }
            }
          }}
          onBlur={() => {
            setPriceInput(parseDecimal(priceInput, 2).toFixed(2));
          }}
          size="small"
          fullWidth
          sx={{
            "& input": { textAlign: "right" },
          }}
        />
      </TableCell>
    </TableRow>
  );
};

export default PartOtherProcessDataEdit;
